import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CheckCircle2, Clock3, CreditCard, Download, Loader2, Mail, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  invoiceQueryOptions,
  startInvoiceCheckout,
  type PublicInvoice,
  type PublicInvoiceAddress,
} from '@/api/invoice'
import { apiUrl, QuoteLinkError } from '@/api/quote'
import { Button } from '@/components/ui/button'
import { ctaButton, secondaryButton } from '@/lib/brand'
import { cn, formatMoney } from '@/lib/utils'

export const Route = createFileRoute('/i/$token')({
  component: CustomerInvoiceRoute,
  validateSearch: (search: Record<string, unknown>): { paid?: boolean } =>
    search.paid === 1 || search.paid === '1' || search.paid === true ? { paid: true } : {},
})

const dateFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const CONFIRM_FOR_MS = 30_000

function CustomerInvoiceRoute() {
  const { token } = Route.useParams()
  const { paid: returned } = Route.useSearch()
  // After Stripe sends the customer back, keep checking for the payment for a short while.
  const [waiting, setWaiting] = useState(Boolean(returned))
  useEffect(() => {
    if (!waiting) return
    const timer = setTimeout(() => setWaiting(false), CONFIRM_FOR_MS)
    return () => clearTimeout(timer)
  }, [waiting])
  const invoice = useQuery({
    ...invoiceQueryOptions(token),
    refetchInterval: (query) => (waiting && query.state.data?.payable ? 2_000 : false),
  })
  if (invoice.isLoading) {
    return (
      <div className="text-muted-foreground grid min-h-[60vh] place-items-center text-sm" role="status">
        <span className="flex items-center gap-2">
          <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
          Opening your invoice
        </span>
      </div>
    )
  }
  if (!invoice.data) return <InvoiceUnavailable />
  const confirming = waiting && invoice.data.payable
  return <CustomerInvoicePage confirming={confirming} invoice={invoice.data} token={token} />
}

function CustomerInvoicePage({
  invoice,
  token,
  confirming,
}: {
  invoice: PublicInvoice
  token: string
  confirming: boolean
}) {
  const facts = [
    { label: 'Issued', value: invoice.issued_at ? dateFormat.format(new Date(invoice.issued_at)) : null },
    { label: 'Due', value: invoice.due_at ? dateFormat.format(new Date(invoice.due_at)) : null },
    { label: 'Terms', value: invoice.terms },
    { label: 'Quote', value: invoice.quote_number },
  ].filter((fact) => fact.value)
  // An invoice cut for a customer alone is titled by its memo; say it once.
  const memo = invoice.memo && invoice.memo !== invoice.title ? invoice.memo : null
  return (
    <div className="bg-usa-mist sm:px-5 sm:py-8">
      <div className="bg-card mx-auto max-w-5xl overflow-hidden sm:rounded-xl sm:border sm:shadow-[0_8px_30px_rgba(17,24,39,0.06)]">
        <header className="border-b px-5 pt-7 pb-6 sm:px-10 sm:pt-9">
          <div className="grid min-w-0 gap-5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
            <div className="min-w-0">
              <p className="text-muted-foreground mb-2 text-sm [overflow-wrap:anywhere]">
                Invoice {invoice.number}
                {invoice.label !== 'Invoice' ? ` · ${invoice.label}` : null}
              </p>
              <h1 className="text-usa-navy text-3xl leading-tight font-extrabold tracking-tight [overflow-wrap:anywhere] sm:text-4xl">
                {invoice.title}
              </h1>
              <span aria-hidden="true" className="bg-usa-red mt-4 block h-[3px] w-16" />
            </div>
            <Button asChild className={secondaryButton} variant="outline">
              {/* eslint-disable-next-line no-restricted-syntax -- PDF from the API */}
              <a href={apiUrl(invoice.document_url)} rel="noreferrer" target="_blank">
                <Download aria-hidden="true" />
                Download PDF
              </a>
            </Button>
          </div>
        </header>
        <div className="grid lg:grid-cols-[minmax(0,1fr)_340px]">
          <article className="min-w-0 px-5 py-7 sm:px-10 sm:py-9">
            <dl className="grid grid-cols-2 gap-4 border-b pb-6 text-sm sm:grid-cols-4">
              {facts.map((fact) => (
                <div key={fact.label}>
                  <dt className="text-muted-foreground text-xs">{fact.label}</dt>
                  <dd className="mt-0.5 font-medium">{fact.value}</dd>
                </div>
              ))}
            </dl>
            <div className="grid gap-4 border-b py-6 text-sm sm:grid-cols-2">
              <Address address={invoice.bill_to} fallback={invoice.customer_name} label="Bill to" />
              <Address address={invoice.ship_to} label="Ship to" />
            </div>
            <table className="mt-7 w-full text-sm">
              <caption className="sr-only">Items on this invoice</caption>
              <thead>
                <tr className="text-muted-foreground border-b text-left text-xs">
                  <th className="pb-2 font-semibold" scope="col">
                    Item
                  </th>
                  <th className="w-16 pb-2 text-right font-semibold" scope="col">
                    Qty
                  </th>
                  <th className="w-28 pb-2 text-right font-semibold" scope="col">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y border-b">
                {invoice.items.map((item, index) => (
                  <tr key={`${index}-${item.description}`}>
                    <td className="py-3 pr-3 break-words">
                      {item.model || item.manufacturer ? (
                        <p className="font-medium">{[item.manufacturer, item.model].filter(Boolean).join(' ')}</p>
                      ) : null}
                      <p className={item.model || item.manufacturer ? 'text-muted-foreground mt-0.5 whitespace-pre-line' : 'whitespace-pre-line'}>
                        {item.description}
                      </p>
                    </td>
                    <td className="py-3 text-right tabular-nums">{item.quantity ? Number(item.quantity) : ''}</td>
                    <td className="py-3 text-right tabular-nums">{formatMoney(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <dl className="ml-auto mt-4 grid max-w-sm gap-2 text-sm tabular-nums">
              <Row label="Subtotal" value={invoice.subtotal} />
              {Number(invoice.discount) ? <Row label="Discount" value={invoice.discount} /> : null}
              <Row label="Shipping" value={invoice.shipping} />
              <Row label="Tax" value={invoice.tax} />
              {invoice.total !== invoice.quote_total ? (
                <>
                  <Row label="Quote total" value={invoice.quote_total} />
                  <Row label={`This invoice: ${invoice.label}`} strong value={invoice.total} />
                </>
              ) : (
                <Row label="Invoice total" strong value={invoice.total} />
              )}
              {Number(invoice.credited) > 0 ? <Row label="Credited" value={`-${invoice.credited}`} /> : null}
              {Number(invoice.amount_paid) > 0 ? <Row label="Paid" value={`-${invoice.amount_paid}`} /> : null}
              <div className="bg-usa-tint text-usa-navy flex justify-between gap-3 rounded-[4px] px-3 py-2 font-bold">
                <dt>Amount due</dt>
                <dd>{formatMoney(invoice.status === 'void' ? 0 : invoice.amount_remaining)}</dd>
              </div>
            </dl>
            {memo ? <p className="text-muted-foreground mt-6 text-sm whitespace-pre-line">{memo}</p> : null}
          </article>
          <aside
            aria-labelledby="invoice-payment"
            className="bg-muted/40 border-t px-5 py-8 sm:px-8 lg:border-t-0 lg:border-l"
          >
            <h2 className="text-base font-semibold" id="invoice-payment">
              Payment
            </h2>
            <PaymentPanel confirming={confirming} invoice={invoice} token={token} />
            <div className="mt-7 border-t pt-6 text-sm">
              <p className="text-muted-foreground text-xs">Questions about this invoice</p>
              {/* eslint-disable-next-line no-restricted-syntax -- mailto link */}
              <a
                className="text-usa-blue mt-1 inline-flex items-center gap-1.5 break-all hover:underline"
                href={`mailto:${invoice.contact_email}?subject=${encodeURIComponent(`Invoice ${invoice.number}`)}`}
              >
                <Mail aria-hidden="true" className="size-3.5 shrink-0" />
                {invoice.contact_email}
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

function PaymentPanel({
  invoice,
  token,
  confirming,
}: {
  invoice: PublicInvoice
  token: string
  confirming: boolean
}) {
  const pay = useMutation({
    mutationFn: () => startInvoiceCheckout(token),
    onSuccess: ({ url }) => window.location.assign(url),
  })
  // Keep the button busy while the browser leaves for Stripe.
  const leaving = pay.isPending || pay.isSuccess
  const { reset } = pay
  useEffect(() => {
    // Coming back with the browser's back button restores this page from cache.
    const onShow = (event: PageTransitionEvent) => event.persisted && reset()
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [reset])

  if (invoice.status === 'paid') {
    return (
      <div className="mt-3 flex items-start gap-2.5 text-sm">
        <CheckCircle2 aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-[#15803d]" />
        <div>
          <p className="font-semibold">Paid in full</p>
          {invoice.paid_at ? (
            <p className="text-muted-foreground">{dateFormat.format(new Date(invoice.paid_at))}. Thank you.</p>
          ) : null}
        </div>
      </div>
    )
  }
  if (invoice.status === 'void') {
    return <p className="text-muted-foreground mt-3 text-sm">This invoice was canceled. Nothing is due.</p>
  }
  if (invoice.payment_processing) {
    return (
      <div className="mt-3 flex items-start gap-2.5 text-sm" role="status">
        <Clock3 aria-hidden="true" className="text-usa-blue mt-0.5 size-5 shrink-0" />
        <div>
          <p className="font-semibold">Bank payment processing</p>
          <p className="text-muted-foreground">Bank payments take up to 4 business days to clear.</p>
        </div>
      </div>
    )
  }
  if (confirming) {
    return (
      <p className="text-muted-foreground mt-3 flex items-center gap-2 text-sm" role="status">
        <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
        Confirming your payment
      </p>
    )
  }
  if (!invoice.payable) {
    return <p className="text-muted-foreground mt-3 text-sm">Contact us about paying this invoice.</p>
  }
  return (
    <div className="bg-card mt-3 rounded-lg border p-4 shadow-sm">
      <p className="text-muted-foreground text-xs">Amount due</p>
      <p className="text-usa-navy mt-1 text-2xl font-extrabold tabular-nums">{formatMoney(invoice.amount_remaining)}</p>
      {invoice.stripe_pay_url ? (
        <Button asChild className={cn(ctaButton, 'mt-3 w-full')}>
          {/* eslint-disable-next-line no-restricted-syntax -- Stripe's page for an invoice Stripe issued */}
          <a href={invoice.stripe_pay_url}>
            <CreditCard aria-hidden="true" />
            Pay invoice
          </a>
        </Button>
      ) : (
        <Button
          className={cn(ctaButton, 'mt-3 w-full')}
          disabled={leaving}
          onClick={() => pay.mutate()}
        >
          {leaving ? (
            <Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
          ) : (
            <CreditCard aria-hidden="true" />
          )}
          Pay invoice
        </Button>
      )}
      <p className="text-muted-foreground mt-2 text-center text-xs">Card or bank payment</p>
      {pay.error ? (
        <p className="text-destructive mt-2 text-sm" role="alert">
          {pay.error instanceof QuoteLinkError && pay.error.code === 'payment_processing'
            ? 'A bank payment for this invoice is already processing.'
            : 'Payment could not be started. Try again.'}
        </p>
      ) : null}
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={strong ? 'flex justify-between gap-3 font-semibold' : 'flex justify-between gap-3'}>
      <dt className={strong ? undefined : 'text-muted-foreground'}>{label}</dt>
      <dd>{formatMoney(value)}</dd>
    </div>
  )
}

function Address({
  label,
  address,
  fallback,
}: {
  label: string
  address: PublicInvoiceAddress | null
  fallback?: string
}) {
  if (!address && !fallback) return null
  const place = address
    ? [address.city, [address.state, address.postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    : null
  return (
    <div className="min-w-0 break-words">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="mt-0.5 font-medium">{address?.name || fallback}</p>
      {address?.contact_name ? <p>{address.contact_name}</p> : null}
      {address?.line1 ? <p>{address.line1}</p> : null}
      {address?.line2 ? <p>{address.line2}</p> : null}
      {place ? <p>{place}</p> : null}
    </div>
  )
}

function InvoiceUnavailable() {
  return (
    <div className="grid min-h-[60vh] place-items-center p-5">
      <div className="bg-card max-w-sm rounded-xl border p-7 text-center shadow-sm">
        <span className="border-destructive/30 text-destructive mx-auto grid size-10 place-items-center rounded-full border">
          <X aria-hidden="true" className="size-4" />
        </span>
        <h1 className="mt-4 text-lg font-semibold">Invoice unavailable</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          This invoice link is not active. Contact the person who sent it for a new one.
        </p>
      </div>
    </div>
  )
}
